import axios from 'axios';

// Ensure API_URL always ends with /api
const getApiUrl = () => {
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
  return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Stats {
  totalQuestions: number;
  answeredQuestions: number;
  avgResponseTime: number;
  conversionRate: number;
  recentQuestions: Array<{
    question: string;
    createdAt: string;
  }>;
}

export interface Analytics {
  timeline: Array<{
    date: string;
    questions: number;
    conversions: number;
  }>;
  topCategories: Array<{
    name: string;
    count: number;
  }>;
  popularProducts: Array<{
    name: string;
    views: number;
  }>;
}

export interface Store {
  id: string;
  name: string;
  domain: string | null;
  api_key: string;
  created_at: string;
  updated_at: string;
  total_questions?: number;
  answered_questions?: number;
  avg_response_time?: number;
}

export interface WidgetConfig {
  id: string;
  store_id: string;
  primary_color: string;
  background_color: string;
  text_color: string;
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  language: string;
  greeting: string;
  placeholder: string;
  settings: any;
  created_at: string;
  updated_at: string;
}

export async function fetchStats(): Promise<Stats> {
  const { data } = await api.get('/stats');
  return data;
}

export async function fetchAnalytics(): Promise<Analytics> {
  const { data } = await api.get('/analytics');
  return data;
}

export async function updateSettings(settings: any) {
  const { data } = await api.put('/settings', settings);
  return data;
}

// Stores API
export async function fetchStores(): Promise<{ stores: Store[] }> {
  const { data } = await api.get('/stores');
  return data;
}

export async function createStore(storeData: { name: string; domain?: string }): Promise<{ store: Store }> {
  const { data } = await api.post('/stores', storeData);
  return data;
}

export async function fetchStore(id: string): Promise<{ store: Store }> {
  const { data } = await api.get(`/stores/${id}`);
  return data;
}

export async function updateStore(id: string, storeData: { name: string; domain?: string }): Promise<{ store: Store }> {
  const { data } = await api.put(`/stores/${id}`, storeData);
  return data;
}

export async function deleteStore(id: string): Promise<{ message: string }> {
  const { data } = await api.delete(`/stores/${id}`);
  return data;
}

export async function fetchStoreStats(id: string): Promise<Stats> {
  const { data } = await api.get(`/stores/${id}/stats`);
  return data;
}

// Widget Config API
export async function fetchWidgetConfig(storeId: string): Promise<{ config: WidgetConfig }> {
  const { data } = await api.get(`/stores/${storeId}/widget-config`);
  return data;
}

export async function updateWidgetConfig(storeId: string, config: Partial<WidgetConfig>): Promise<{ config: WidgetConfig }> {
  const { data } = await api.put(`/stores/${storeId}/widget-config`, config);
  return data;
}

// Product interfaces
export interface Product {
  id: string;
  store_id: string;
  name: string;
  description?: string;
  price?: number;
  currency?: string;
  image_url?: string;
  product_url?: string;
  sku?: string;
  category?: string;
  in_stock?: boolean;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

// Products API
export async function fetchProducts(storeId: string): Promise<{ products: Product[] }> {
  const { data } = await api.get(`/products?storeId=${storeId}`);
  return data;
}

export async function createProduct(productData: Partial<Product>): Promise<{ product: Product }> {
  const { data } = await api.post('/products', productData);
  return data;
}

export async function updateProduct(id: string, productData: Partial<Product>): Promise<{ product: Product }> {
  const { data } = await api.put(`/products/${id}`, productData);
  return data;
}

export async function deleteProduct(id: string): Promise<{ message: string }> {
  const { data } = await api.delete(`/products/${id}`);
  return data;
}

export async function bulkCreateProducts(storeId: string, products: Partial<Product>[]): Promise<{ products: Product[] }> {
  const { data } = await api.post('/products/bulk', { store_id: storeId, products });
  return data;
}

export async function importProductsFromFeed(
  storeId: string,
  feedUrl: string,
  format: string = 'google-shopping'
): Promise<{ success: boolean; imported: number; products: Product[] }> {
  const { data } = await api.post('/products/import', { storeId, feedUrl, format });
  return data;
}

export default api;
